import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Button, TextField } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChatMessage from '@src/components/Main/Chat/ChatMessage/ChatMessage';
import {
	DescribedInteractive,
	InteractiveParameter,
} from '@src/components/InteractiveMessageList/InteractiveMessageList';
import { isEmptyString } from '@src/helpers/Helpers';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
	Button as ReplyButton,
	CarouselCard,
	ListSection,
	Message,
} from '@src/types/messages';
import { fromInteractive } from '@src/helpers/MessageHelper';
import ListSectionsEditor from './ListSectionsEditor';
import ReplyButtonsEditor from './ReplyButtonsEditor';
import CarouselCardsEditor, {
	getCardMediaLink,
	MIN_CAROUSEL_CARDS,
} from './CarouselCardsEditor';
import {
	Advanced,
	AdvancedToggle,
	Container,
	Description,
	HelperText,
	PreviewContainer,
	PreviewTitle,
	StyledAlert,
	TextFieldWrapper,
} from './SendInteractiveMessageDialog.styles';

export type Props = {
	isVisible: boolean;
	setVisible: (visible: boolean) => void;
	describedInteractive?: DescribedInteractive;
	onSend: (interactiveMessage: any) => void;
};

const SendInteractiveMessageDialog: React.FC<Props> = ({
	isVisible,
	setVisible,
	describedInteractive,
	onSend,
}) => {
	const [payload, setPayload] = useState<any>({});
	const [messageData, setMessageData] = useState<Message | null>(null);
	const [isShowErrors, setIsShowErrors] = useState(false);
	const [isShowingAdvanced, setIsShowingAdvanced] = useState(false);

	const { t } = useTranslation();

	useEffect(() => {
		if (isVisible) {
			setPayload(
				describedInteractive?.payload ? { ...describedInteractive.payload } : {}
			);
			setIsShowErrors(false);
			setIsShowingAdvanced(false);
		}
	}, [isVisible, describedInteractive]);

	useEffect(() => {
		if (payload) {
			setMessageData(fromInteractive({ ...payload }));
		}
	}, [payload]);

	const close = () => {
		setVisible(false);
	};

	const checkAndSend = () => {
		const cloneObj = JSON.parse(JSON.stringify(payload));

		if (!isFormValid()) {
			setIsShowErrors(true);
			return;
		}

		// Removing header if text is empty
		if (cloneObj.header && isEmptyString(cloneObj.header.text)) {
			delete cloneObj.header;
		}

		// Removing footer if text is empty
		if (cloneObj.footer && isEmptyString(cloneObj.footer.text)) {
			delete cloneObj.footer;
		}

		// Reply button ids are required by the API but meaningless to compose
		// by hand, so assign them sequentially
		if (cloneObj.type === 'button' && Array.isArray(cloneObj.action?.buttons)) {
			cloneObj.action.buttons = cloneObj.action.buttons.map(
				(button: any, index: number) => ({
					...button,
					reply: { ...button.reply, id: `button_${index + 1}` },
				})
			);
		}

		// Carousel cards are ordered by a required index, so assign it
		// sequentially and drop the empty optional card bodies
		if (cloneObj.type === 'carousel' && Array.isArray(cloneObj.action?.cards)) {
			cloneObj.action.cards = cloneObj.action.cards.map(
				(card: any, index: number) => {
					const cleanCard = { ...card, card_index: index };
					if (cleanCard.body && isEmptyString(cleanCard.body.text)) {
						delete cleanCard.body;
					}
					return cleanCard;
				}
			);
		}

		// List rows require an id, but composing one by hand is meaningless,
		// so assign sequential ids and drop the empty optional fields
		if (cloneObj.type === 'list' && Array.isArray(cloneObj.action?.sections)) {
			let rowNumber = 0;
			cloneObj.action.sections = cloneObj.action.sections.map(
				(section: any) => {
					const cleanSection = {
						...section,
						rows: section.rows.map((row: any) => {
							rowNumber += 1;
							const cleanRow = { ...row, id: `row_${rowNumber}` };
							if (isEmptyString(cleanRow.description)) {
								delete cleanRow.description;
							}
							return cleanRow;
						}),
					};
					if (isEmptyString(cleanSection.title)) {
						delete cleanSection.title;
					}
					return cleanSection;
				}
			);
		}

		onSend(cloneObj);
		close();
	};

	function keyToLabel(str?: string | null): string {
		if (!str) {
			return '';
		}

		const replacedStr = str.replace(/[._]/g, ' '); // Replace dots and underscores with spaces
		return replacedStr.charAt(0).toUpperCase() + replacedStr.slice(1);
	}

	function getNestedValue(obj: any, path: string) {
		return path.split('.').reduce((acc, key) => acc && acc[key], obj);
	}

	function setNestedValue(obj: any, path: string, value: any) {
		const keys = path.split('.');
		const lastKey = keys.pop(); // Get the last key (e.g., 'display_text')

		const cloneObj = JSON.parse(JSON.stringify(obj));

		// Traverse the object to the second-to-last key
		const nestedObj = keys.reduce((acc, key) => acc && acc[key], cloneObj);

		if (nestedObj && lastKey) {
			nestedObj[lastKey] = value; // Set the new value
		}

		return cloneObj;
	}

	const isFormValid = () => {
		let isValid = true;

		describedInteractive?.parameters.forEach((parameter) => {
			if (
				parameter.required &&
				isEmptyString(getNestedValue(payload, parameter.key))
			) {
				isValid = false;
			}

			if (parameter.control === 'replyButtons') {
				const buttons: ReplyButton[] =
					getNestedValue(payload, parameter.key) ?? [];
				if (
					!buttons.length ||
					buttons.some((button) => isEmptyString(button.reply?.title ?? ''))
				) {
					isValid = false;
				}
			}

			if (parameter.control === 'carouselCards') {
				const cards: CarouselCard[] =
					getNestedValue(payload, parameter.key) ?? [];
				if (
					cards.length < MIN_CAROUSEL_CARDS ||
					cards.some(
						(card) =>
							isEmptyString(getCardMediaLink(card)) ||
							isEmptyString(card.action?.parameters?.display_text ?? '') ||
							isEmptyString(card.action?.parameters?.url ?? '')
					)
				) {
					isValid = false;
				}
			}

			if (parameter.control === 'listSections') {
				const sections: ListSection[] =
					getNestedValue(payload, parameter.key) ?? [];
				const rows = sections.flatMap((section) => section.rows ?? []);
				if (!rows.length || rows.some((row) => isEmptyString(row.title))) {
					isValid = false;
				}
				// Section titles are only mandatory when there are multiple sections
				if (
					sections.length > 1 &&
					sections.some((section) => isEmptyString(section.title ?? ''))
				) {
					isValid = false;
				}
			}
		});

		return isValid;
	};

	const renderInput = (parameter: InteractiveParameter) => {
		if (parameter.control === 'replyButtons') {
			return (
				<ReplyButtonsEditor
					key={parameter.key}
					buttons={getNestedValue(payload, parameter.key) ?? []}
					onChange={(buttons) =>
						setPayload((prevState: any) =>
							setNestedValue(prevState, parameter.key, buttons)
						)
					}
					isShowErrors={isShowErrors}
				/>
			);
		}

		if (parameter.control === 'carouselCards') {
			return (
				<CarouselCardsEditor
					key={parameter.key}
					cards={getNestedValue(payload, parameter.key) ?? []}
					onChange={(cards) =>
						setPayload((prevState: any) =>
							setNestedValue(prevState, parameter.key, cards)
						)
					}
					isShowErrors={isShowErrors}
				/>
			);
		}

		if (parameter.control === 'listSections') {
			return (
				<ListSectionsEditor
					key={parameter.key}
					sections={getNestedValue(payload, parameter.key) ?? []}
					onChange={(sections) =>
						setPayload((prevState: any) =>
							setNestedValue(prevState, parameter.key, sections)
						)
					}
					isShowErrors={isShowErrors}
				/>
			);
		}

		return (
			<TextFieldWrapper key={parameter.key}>
				<TextField
					variant="standard"
					value={getNestedValue(payload, parameter.key)}
					onChange={(e) =>
						setPayload((prevState: any) =>
							setNestedValue(prevState, parameter.key, e.target.value)
						)
					}
					label={t(parameter.placeholder || keyToLabel(parameter.key))}
					size="small"
					multiline={true}
					fullWidth={true}
					required={parameter.required}
					inputProps={
						parameter.maxLength ? { maxLength: parameter.maxLength } : undefined
					}
					error={
						isShowErrors && parameter.required
							? isEmptyString(getNestedValue(payload, parameter.key) ?? '')
							: false
					}
				/>
				{parameter.description && (
					<HelperText
						dangerouslySetInnerHTML={{
							__html: t(parameter.description),
						}}
					/>
				)}
			</TextFieldWrapper>
		);
	};

	return (
		<Dialog open={isVisible} onClose={close}>
			<DialogTitle>
				{t(describedInteractive?.title ?? 'Send an interactive message')}
			</DialogTitle>
			<DialogContent>
				{describedInteractive && (
					<Container>
						<div>
							<Description
								dangerouslySetInnerHTML={{
									__html: t(describedInteractive.description),
								}}
							/>
							{describedInteractive.warning && (
								<StyledAlert severity="warning">
									<div
										dangerouslySetInnerHTML={{
											__html: t(describedInteractive.warning),
										}}
									/>
								</StyledAlert>
							)}
							<div>
								{payload &&
									describedInteractive.parameters
										.filter((parameter) => !parameter.advanced)
										.map((parameter) => renderInput(parameter))}
							</div>

							{payload &&
								describedInteractive.parameters.filter(
									(parameter) => parameter.advanced
								).length > 0 && (
									<AdvancedToggle
										onClick={() =>
											setIsShowingAdvanced((prevState) => !prevState)
										}
									>
										{isShowingAdvanced ? (
											<KeyboardArrowUpIcon />
										) : (
											<KeyboardArrowDownIcon />
										)}
										{t(
											isShowingAdvanced
												? 'Hide advanced settings'
												: 'Show advanced settings'
										)}
									</AdvancedToggle>
								)}
							<Advanced
								style={{ display: isShowingAdvanced ? 'block' : 'none' }}
							>
								{payload &&
									describedInteractive.parameters
										.filter((parameter) => parameter.advanced)
										.map((parameter) => renderInput(parameter))}
							</Advanced>

							{describedInteractive.info && (
								<StyledAlert severity="info">
									<div
										dangerouslySetInnerHTML={{
											__html: t(describedInteractive.info),
										}}
									/>
								</StyledAlert>
							)}
						</div>

						<div>
							<PreviewTitle>{t('Preview')}</PreviewTitle>
							<PreviewContainer>
								{messageData && (
									<ChatMessage
										data={messageData}
										disableMediaPreview
										isInfoClickable={false}
									/>
								)}
							</PreviewContainer>
						</div>
					</Container>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={close} color="secondary">
					{t('Close')}
				</Button>
				<Button onClick={checkAndSend} color="primary" autoFocus>
					{t('Send')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default SendInteractiveMessageDialog;
